import { useState } from "react";
import { ArrowForward, OpenInNew } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useViewAs } from "hooks/useViewAs";
import type { Partner } from "types/partners";
import {
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_SURFACE_HAIRLINE,
} from "components/hoc/Theme";
import PartnerBadge from "./PartnerBadge";

interface PartnerCardProps {
  partner: Partner;
  featured?: boolean;
}

export default function PartnerCard({
  partner,
  featured = false,
}: PartnerCardProps) {
  const navigate = useNavigate();
  const { href } = useViewAs();
  const [searchParams] = useSearchParams();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    const params = new URLSearchParams();
    if (searchParams.get("search"))
      params.set("search", searchParams.get("search")!);
    if (searchParams.get("partner_type"))
      params.set("partner_type", searchParams.get("partner_type")!);
    if (searchParams.get("industry_type"))
      params.set("industry_type", searchParams.get("industry_type")!);
    if (searchParams.get("sort")) params.set("sort", searchParams.get("sort")!);
    if (searchParams.get("filterEverUsed"))
      params.set("filterEverUsed", searchParams.get("filterEverUsed")!);
    const queryString = params.toString();
    navigate(
      href(`/partners/${partner.slug}${queryString ? `?${queryString}` : ""}`),
    );
  };

  return (
    <Box
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "relative",
        aspectRatio: "1 / 1",
        borderRadius: "10px",
        border: isHovered
          ? "1px solid rgba(0,0,0,0.15)"
          : `1px solid ${CALIBRATE_SURFACE_HAIRLINE}`,
        backgroundColor: "background.paper",
        p: {
          xs: "10px",
          sm: "14px",
        },
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: featured
          ? isHovered
            ? "0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)"
            : "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)"
          : isHovered
            ? "0px 4px 12px rgba(0,0,0,0.12), 0px 2px 4px rgba(0,0,0,0.08)"
            : "0px 1px 3px rgba(0,0,0,0.12), 0px 1px 2px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Featured Badge */}
      {partner.is_featured && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <PartnerBadge
            type="featured"
            value="featured"
            label="Featured Partner"
            size="small"
          />
        </Box>
      )}

      {/* Logo — fixed height so all cards have consistent logo zone */}
      <Box
        sx={{
          height: {
            xs: "80px",
            sm: "90px",
            md: "80px",
            lg: "80px",
            xl: "70px",
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: {
            xs: "20px",
            sm: "30px",
          },
        }}
      >
        {partner.logo_url ? (
          <Box
            component="img"
            src={partner.logo_url}
            alt={partner.name}
            sx={{
              maxWidth: "100%",
              maxHeight: {
                xs: "70px",
                sm: "82px",
                md: "72px",
                lg: "72px",
                xl: "65px",
              },
              objectFit: "contain",
            }}
          />
        ) : (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center",
              fontSize: {
                xs: "0.875rem",
                sm: "1rem",
              },
            }}
          >
            {partner.name}
          </Typography>
        )}
      </Box>

      {/* Description */}
      <Box
        sx={{
          width: "100%",
          mx: "auto",
          minHeight: {
            xs: "40px",
            sm: "60px",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            fontSize: {
              xs: "0.8125rem",
              sm: "0.875rem",
              md: "0.8125rem",
              lg: "0.8125rem",
              xl: "0.8125rem",
            },
            color: "text.secondary",
            textAlign: "center",
            lineHeight: 1.4,
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: {
              xs: 1,
              sm: 2,
            },
            WebkitBoxOrient: "vertical",
            mt: {
              xs: "6px",
              sm: "8px",
            },
            mb: {
              xs: "6px",
              sm: "8px",
            },
          }}
        >
          {partner.tile_description}
        </Typography>
      </Box>

      {/* Badges */}
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: {
            xs: "2px",
            sm: "4px",
          },
          flexWrap: "nowrap",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          width: "100%",
          mb: {
            xs: "6px",
            sm: "8px",
          },
        }}
      >
        <PartnerBadge
          type="industry"
          value={partner.industry_type}
          label={partner.industry_type_display}
          size="small"
        />
        <PartnerBadge
          type="partner"
          value={partner.partner_type}
          label={partner.partner_type_display}
          size="small"
        />
      </Box>

      {/* Learn More Link */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 0.5,
          mt: "auto",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            fontSize: {
              xs: isHovered ? "0.875rem" : "0.8125rem",
              sm: isHovered ? "0.9375rem" : "0.875rem",
              md: isHovered ? "1rem" : "0.9375rem",
              lg: isHovered ? "1rem" : "0.9375rem",
              xl: isHovered ? "0.85rem" : "0.8rem",
            },
            fontWeight: 700,
            color: "rgba(31,41,51,0.9)",
            transition: "font-size 0.2s ease",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          Learn More
          <ArrowForward
            sx={{
              fontSize: {
                xs: isHovered ? 14 : 13,
                sm: isHovered ? 16 : 15,
                md: isHovered ? 17 : 16,
                lg: isHovered ? 17 : 16,
                xl: isHovered ? 15 : 14,
              },
              transition: "all 0.2s ease",
              transform: isHovered ? "translateX(2px)" : "none",
            }}
          />
        </Typography>
      </Box>
    </Box>
  );
}
